import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  LEGAL_FORM_CATALOGUE,
  INTERNATIONAL_CATALOGUE_VERSION,
  LEGACY_ELF_CODES,
  findLegalForm,
  legalFormApplies,
  legalFormLabel,
} from '@erp/shared';
import { CatalogueSelect } from './catalogue-select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

interface LegalFormValue {
  legalFormCode?: string | null | undefined;
  legalFormName?: string | null | undefined;
  legalFormJurisdiction?: string | null | undefined;
}
export function LegalFormSelect({
  id,
  country,
  legacy,
  value,
  onChange,
  disabled = false,
}: {
  id: string;
  country: string;
  legacy?: string | null | undefined;
  value: LegalFormValue;
  onChange: (value: LegalFormValue, replacesSelection: boolean) => void;
  disabled?: boolean;
}): JSX.Element {
  const { t, i18n } = useTranslation();
  const scope = value.legalFormJurisdiction || country;
  const mapped = legacy ? LEGACY_ELF_CODES[legacy] : undefined;
  const selectedCode =
    value.legalFormCode ?? (mapped || (legacy && legacy !== 'SONSTIGE' ? `legacy:${legacy}` : ''));
  const options = useMemo(
    () =>
      LEGAL_FORM_CATALOGUE.filter(
        (row) => row.active && (row.jurisdiction === scope || row.country === scope),
      )
        .map((row) => ({
          id: row.code,
          label: legalFormLabel(row.code),
          hint: row.code.startsWith('FR:')
            ? `${row.jurisdictionName} · Service-Public`
            : row.jurisdictionName,
          search: [row.code, row.name, row.abbreviation, ...row.aliases].join(' '),
        }))
        .sort((a, b) => a.label.localeCompare(b.label, i18n.language)),
    [scope, i18n.language],
  );
  const allOptions = [
    ...options,
    { id: 'OTHER', label: t('international.otherLegalForm'), hint: '' },
  ];
  if (selectedCode && !allOptions.some((row) => row.id === selectedCode))
    allOptions.unshift({
      id: selectedCode,
      label: selectedCode.startsWith('legacy:')
        ? t(`company.legalForms.${legacy}`)
        : legalFormLabel(selectedCode),
      hint: t('international.legalMismatch'),
    });
  const jurisdictions = useMemo(
    () =>
      [
        ...new Map(
          LEGAL_FORM_CATALOGUE.map((row) => [
            row.jurisdiction,
            {
              id: row.jurisdiction,
              label: `${row.jurisdictionName} · ${row.jurisdiction}`,
            },
          ]),
        ).values(),
      ].sort((a, b) => a.label.localeCompare(b.label, i18n.language)),
    [i18n.language],
  );
  const mismatch =
    selectedCode && !legalFormApplies(selectedCode, country, value.legalFormJurisdiction);
  return (
    <div className="space-y-2">
      <CatalogueSelect
        id={id}
        label={t('international.legalForm')}
        options={allOptions}
        value={selectedCode}
        disabled={disabled}
        onChange={(values) => onChange({ ...value, legalFormCode: values[0] ?? null }, true)}
      />
      {mismatch ? (
        <p role="status" className="text-sm text-destructive">
          {t('international.legalMismatch')}
        </p>
      ) : null}
      {options.length === 0 ? (
        <p className="text-sm text-muted-foreground">{t('international.noCoverage')}</p>
      ) : null}
      {selectedCode === 'OTHER' ? (
        <div>
          <Label htmlFor={`${id}-other`}>{t('international.otherName')}</Label>
          <Input
            id={`${id}-other`}
            required
            disabled={disabled}
            value={value.legalFormName ?? ''}
            onChange={(event) => onChange({ ...value, legalFormName: event.target.value }, false)}
          />
        </div>
      ) : null}
      <details
        open={value.legalFormJurisdiction ? true : undefined}
        className="text-xs text-muted-foreground"
      >
        <summary className="cursor-pointer py-2 focus-visible:ring-2 focus-visible:ring-ring">
          {t('international.jurisdiction')}
        </summary>
        <CatalogueSelect
          label={t('international.jurisdiction')}
          value={value.legalFormJurisdiction ?? ''}
          options={jurisdictions}
          disabled={disabled}
          emptyValueLabel={t('international.seatJurisdiction')}
          onChange={(values) =>
            onChange({ ...value, legalFormJurisdiction: values[0] ?? null }, false)
          }
        />
        <p className="pt-1">{t('international.jurisdictionHint')}</p>
      </details>
      <p className="text-xs text-muted-foreground">
        {INTERNATIONAL_CATALOGUE_VERSION}
        {findLegalForm(selectedCode)?.jurisdiction
          ? ` · ${findLegalForm(selectedCode)?.jurisdiction}`
          : ''}
      </p>
    </div>
  );
}
